/* =========================================================
   cart-marketing-prices.js - SISTEMA 100% VISUAL PURO v3.1
   ========================================================= */

// Formatear precio en euros
const fmtEUR = (cents) => {
  if (!cents || isNaN(cents)) return '0,00€';
  const amount = ((cents || 0) / 100).toFixed(2).replace('.', ',');
  return `${amount}€`;
};

// Utilidades
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// Configuración crítica
const GIFT_VARIANT_ID = 51390528913746;
const GIFT_REAL_COMPARE_PRICE = 1795; // 17,95€
const FREE_SHIPPING_THRESHOLD = 4000; // 40€ en centavos

// Config burbuja carrito
const BUBBLE_COUNT_INCLUDES_GIFTS = true;
const HIDE_BUBBLE_WHEN_ZERO = true;

// Estados de operación
let __operationInProgress = false;
let __skipEmptyRenderUntil = 0;
let __normalizationInProgress = false;

/* ==================== DETECCIÓN INTELIGENTE UNIFICADA ==================== */

function detectProductType(handle, productData = null) {
  if (!handle) return 'multicantidad_simple';
  
  const lowerHandle = handle.toLowerCase();
  
  if (lowerHandle.includes('arnes')) return 'arnes';
  if (lowerHandle.includes('cama')) return 'cama';
  
  if (lowerHandle.includes('bebedero') || lowerHandle.includes('guante') || lowerHandle.includes('correa')) {
    if (productData && productData.variants && productData.variants.length === 6) {
      return 'multicantidad_con_variantes';
    }
    return 'multicantidad_simple';
  }
  
  const currentHandle = getCurrentProductHandle();
  if (handle === currentHandle) {
    const metafieldElement = document.querySelector('[data-product-type]');
    if (metafieldElement) {
      const metaValue = metafieldElement.dataset.productType;
      
      if (metaValue === 'ninguno') return 'arnes';
      if (metaValue === 'dropdown_descuento') return 'cama';  
      if (metaValue === 'dropdown_simple') return 'bebedero_guante_correa';
      
      if (metaValue === '') {
        const quantityElement = document.querySelector('[data-quantity-display-names]');
        if (quantityElement) {
          const namesCount = quantityElement.dataset.quantityDisplayNames
            .split('\n').filter(name => name.trim() !== '').length;
          return namesCount === 6 ? 'multicantidad_con_variantes' : 'multicantidad_simple';
        }
      }
    }
  }
  
  return 'multicantidad_simple';
}

function getCurrentProductHandle() {
  const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
  return pathMatch ? pathMatch[1] : null;
}

/* ==================== FUNCIONES DE CARRITO BASE ==================== */

async function getCart() {
  try {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('No se pudo obtener el carrito');
    return res.json();
  } catch (e) {
    return { items: [], item_count: 0, total_price: 0 };
  }
}

function isArnesCartItem(item) {
  const handle = extractHandleFromItem(item);
  return detectProductType(handle) === 'arnes';
}

function isGiftCartItem(item) {
  return !!(item.properties && item.properties._regalo_arnes === 'true');
}

function extractHandleFromItem(item) {
  return item.product_handle ||
         item.handle ||
         ((item.url || '').match(/\/products\/([^\/?#]+)/)?.[1]) ||
         null;
}

function getTotalArnesQty(cart) {
  return cart.items.reduce((sum, item) => {
    return sum + (isArnesCartItem(item) ? item.quantity : 0);
  }, 0);
}

function getGiftLine(cart) {
  return cart.items.find(isGiftCartItem) || null;
}

/* ==================== CÁLCULOS VISUALES PUROS ==================== */

function calculateArnesVisualPrice(currentVariantPrice, quantity = 1) {
  const finalPrice = currentVariantPrice * quantity;
  const originalPrice = Math.round(finalPrice * 1.40); // Marketing: +40%
  
  return {
    finalPrice,
    originalPrice,
    hasDiscount: true,
    savings: originalPrice - finalPrice,
    unitFinalPrice: currentVariantPrice,
    unitOriginalPrice: Math.round(currentVariantPrice * 1.40)
  };
}

function calculateCamaVisualPrice(currentVariantPrice, quantity = 1) {
  const finalPrice = currentVariantPrice * quantity;
  const originalPrice = Math.round(finalPrice * 1.15); // Marketing: +15%
  
  return {
    finalPrice,
    originalPrice,
    hasDiscount: true,
    savings: originalPrice - finalPrice,
    unitFinalPrice: currentVariantPrice,
    unitOriginalPrice: Math.round(currentVariantPrice * 1.15)
  };
}

function calculateMultiQuantityVisualPrice(basePrice, quantity) {
  if (!basePrice || quantity < 1) {
    return { 
      finalPrice: 0, originalPrice: 0, hasDiscount: false, savings: 0,
      unitFinalPrice: 0, unitOriginalPrice: 0 
    };
  }
  
  let finalPrice, originalPrice, hasDiscount = false;
  
  switch (quantity) {
    case 1:
      finalPrice = basePrice;
      originalPrice = basePrice;
      hasDiscount = false;
      break;
    case 2:
      originalPrice = basePrice * 2;
      finalPrice = Math.round(originalPrice * 0.9); // -10%
      hasDiscount = true;
      break;
    case 3:
    default:
      const qty = Math.max(3, quantity);
      originalPrice = basePrice * qty;
      finalPrice = Math.round(originalPrice * 0.8); // -20%
      hasDiscount = true;
      break;
  }
  
  return {
    finalPrice,
    originalPrice,
    hasDiscount,
    savings: hasDiscount ? originalPrice - finalPrice : 0,
    unitFinalPrice: Math.round(finalPrice / quantity),
    unitOriginalPrice: Math.round(originalPrice / quantity)
  };
}

function calculateGiftVisualPrice(quantity = 1) {
  const comparePrice = GIFT_REAL_COMPARE_PRICE * quantity;
  
  return {
    finalPrice: 0, // Siempre gratis
    originalPrice: comparePrice,
    hasDiscount: true,
    savings: comparePrice,
    unitFinalPrice: 0,
    unitOriginalPrice: GIFT_REAL_COMPARE_PRICE
  };
}

/* ==================== SISTEMA DE NOMBRES BONITOS ==================== */

const __NiceLabelCache = Object.create(null);
const __ProductCache = Object.create(null);

function normalizeCode(s) {
  if (!s) return '';
  return String(s).split('/')[0].trim().toUpperCase();
}

async function getProductJSON(handle) {
  if (!handle) return null;
  if (__ProductCache[handle]) return __ProductCache[handle];
  
  try {
    const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
    if (!res.ok) return null;
    const product = await res.json();
    __ProductCache[handle] = product;
    return product;
  } catch {
    return null;
  }
}

async function buildNiceMapForHandle(handle) {
  if (!handle) return { byCode: {} };
  if (__NiceLabelCache[handle]) return __NiceLabelCache[handle];

  const map = { byCode: {} };

  try {
    const res = await fetch(`/products/${handle}`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('fetch HTML fallido');
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    doc.querySelectorAll('.dropdown-option').forEach(opt => {
      const dataValue = opt.getAttribute('data-value') || '';
      if (!dataValue.includes('|')) return;
      const [pretty, raw] = dataValue.split('|').map(s => s.trim());
      if (pretty && raw) {
        map.byCode[normalizeCode(raw)] = pretty;
      }
    });

    const sizeElement = doc.querySelector('[data-size-options]');
    if (sizeElement && sizeElement.dataset.sizeOptions) {
      sizeElement.dataset.sizeOptions.split('\n')
        .map(line => line.trim())
        .filter(Boolean)
        .forEach(line => {
          const [pretty, raw] = line.split('|').map(s => (s || '').trim());
          if (pretty && raw) {
            map.byCode[normalizeCode(raw)] = pretty;
          }
        });
    }
  } catch (e) {
    // Silenciar errores
  }

  __NiceLabelCache[handle] = map;
  return map;
}

async function resolveNiceLabelForCartItem(item) {
  const propNice = item.properties?.['Selección mostrada'];
  if (propNice && String(propNice).trim()) {
    return String(propNice).trim();
  }

  const handle = extractHandleFromItem(item);
  const map = await buildNiceMapForHandle(handle);

  // MAPEO ESPECÍFICO PARA CAMA
  if (handle === 'cama-para-perros' || handle.includes('cama')) {
    const sizeMapping = {
      '60cm': 'Pequeña (60cm)',
      '70cm': 'Mediana (70cm)', 
      '80cm': 'Grande (80cm)',
      '100cm': 'Extra Grande (100cm)',
      '60': 'Pequeña (60cm)',
      '70': 'Mediana (70cm)',
      '80': 'Grande (80cm)',
      '100': 'Extra Grande (100cm)'
    };
    
    // Buscar en las opciones del item
    const itemOptions = [
      item.options_with_values?.[0]?.value,
      item.options_with_values?.[1]?.value, 
      item.variant_title,
      item.title
    ].filter(Boolean);
    
    for (const option of itemOptions) {
      const optionStr = String(option).trim();
      const sizeMatch = optionStr.match(/(\d+)cm?/);
      if (sizeMatch) {
        const sizeKey = `${sizeMatch[1]}cm`;
        if (sizeMapping[sizeKey]) {
          return sizeMapping[sizeKey];
        }
      }
      
      // También verificar mapeo directo
      if (sizeMapping[optionStr]) {
        return sizeMapping[optionStr];
      }
    }
  }

  const candidates = new Set();
  
  if (Array.isArray(item.options_with_values) && item.options_with_values.length) {
    candidates.add(normalizeCode(item.options_with_values[0]?.value));
  }
  if (item.variant_title) {
    candidates.add(normalizeCode(item.variant_title));
  }
  if (item.title) {
    candidates.add(normalizeCode(item.title.split('-').pop()));
  }

  for (const candidate of candidates) {
    if (candidate && map.byCode[candidate]) {
      return map.byCode[candidate];
    }
  }

  return null;
}

/* ==================== ESTADO DE DRAWER DURANTE NORMALIZACIÓN ==================== */

function showDrawerProcessingState() {
  const drawerHost = document.querySelector('cart-drawer');
  if (!drawerHost) return;

  const currentDrawer = drawerHost.querySelector('#CartDrawer');
  if (!currentDrawer) return;

  const processingOverlay = document.createElement('div');
  processingOverlay.id = 'processing-overlay';
  processingOverlay.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(2px);
  `;

  processingOverlay.innerHTML = `
    <div style="text-align: center;">
      <div style="
        width: 40px;
        height: 40px;
        border: 3px solid #f3f3f3;
        border-top: 3px solid #333;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 20px;
      "></div>
      <h3 style="margin: 0 0 10px; color: #333; font-size: 18px;">Procesando pedido...</h3>
      <p style="margin: 0; color: #666; font-size: 14px;">Preparando tu checkout</p>
    </div>
    <style>
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    </style>
  `;

  currentDrawer.style.position = 'relative';
  currentDrawer.appendChild(processingOverlay);
  currentDrawer.style.pointerEvents = 'none';
  processingOverlay.style.pointerEvents = 'auto';
}

function hideDrawerProcessingState() {
  const overlay = document.querySelector('#processing-overlay');
  if (overlay) {
    overlay.remove();
  }

  const drawerHost = document.querySelector('cart-drawer');
  if (drawerHost) {
    const currentDrawer = drawerHost.querySelector('#CartDrawer');
    if (currentDrawer) {
      currentDrawer.style.pointerEvents = '';
    }
  }
}

/* ==================== PROCESAMIENTO VISUAL DEL DRAWER ==================== */

async function processDrawerHTMLVisual(htmlString, cart) {
  try {
    if (__normalizationInProgress) {
      return htmlString;
    }

    if (!cart || !Array.isArray(cart.items)) return htmlString;

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const cartItems = doc.querySelectorAll('.cart-item');
    const max = Math.min(cart.items.length, cartItems.length);

    let totalSavingsCents = 0;
    let totalVisualPrice = 0;

    function ensurePriceWrapper(itemNode) {
      let priceWrapper = itemNode.querySelector('.cart-item__price-wrapper');
      if (!priceWrapper) {
        const host = itemNode.querySelector('.cart-item__totals') ||
                     itemNode.querySelector('.cart-item__details') ||
                     itemNode;
        priceWrapper = doc.createElement('div');
        priceWrapper.className = 'cart-item__price-wrapper';
        host.appendChild(priceWrapper);
      }
      return priceWrapper;
    }

    function hideShopifyPriceNodes(itemNode) {
      const selectors = [
        '.price:not(.cart-item__price-wrapper .price)',
        '.cart-item__price:not(.cart-item__price-wrapper *)',
        '.money:not(.cart-item__price-wrapper *)'
      ];
      selectors.forEach(sel => {
        itemNode.querySelectorAll(sel).forEach(el => {
          if (!el.closest('.cart-item__price-wrapper')) {
            el.style.display = 'none';
          }
        });
      });
    }

    for (let i = 0; i < max; i++) {
      const cartItem = cart.items[i];
      const itemNode = cartItems[i];
      const qty = Number(cartItem.quantity || 1);

      if (isGiftCartItem(cartItem)) {
        itemNode.setAttribute('data-gift', 'true');
        
        const priceData = calculateGiftVisualPrice(qty);
        const priceWrapper = ensurePriceWrapper(itemNode);
        priceWrapper.innerHTML = '';

        priceWrapper.innerHTML = `
          <s style="color:#999;text-decoration:line-through;margin-right:8px;font-size:14px;">
            ${fmtEUR(priceData.originalPrice)}
          </s>
          <span class="price price--end" style="font-weight:bold;font-size:16px;">
            ${fmtEUR(priceData.finalPrice)}
          </span>
          <div class="cart-drawer-item__saving" style="color:#349d92;font-weight:bold;font-size:12px;margin-top:4px;">
            Ahorro De ${fmtEUR(priceData.savings)}
          </div>
        `;

        totalSavingsCents += priceData.savings;
        totalVisualPrice += priceData.finalPrice;
        hideShopifyPriceNodes(itemNode);
        continue;
      }

      try {
        const niceLabel = await resolveNiceLabelForCartItem(cartItem);
        if (niceLabel) {
          const codes = [
            normalizeCode(cartItem.options_with_values?.[0]?.value),
            normalizeCode(cartItem.variant_title)
          ].filter(Boolean);

          const leafElements = itemNode.querySelectorAll('*');
          for (const el of leafElements) {
            if (el.children.length === 0) {
              const text = (el.textContent || '').trim();
              if (codes.includes(normalizeCode(text))) { 
                el.textContent = niceLabel; 
                break; 
              }
            }
          }
        }
      } catch (_) {}

      const handle = extractHandleFromItem(cartItem);
      const productType = detectProductType(handle);
      
      let priceData;
      
      switch (productType) {
        case 'arnes':
          priceData = calculateArnesVisualPrice(cartItem.price, qty);
          break;
          
        case 'cama':
          priceData = calculateCamaVisualPrice(cartItem.price, qty);
          break;
          
        case 'multicantidad_simple':
        case 'multicantidad_con_variantes':
        case 'bebedero_guante_correa':
        default:
          const mqProduct = await getProductJSON(handle);
          const mqBasePrice = mqProduct?.variants?.[0]?.price || cartItem.price;
          priceData = calculateMultiQuantityVisualPrice(mqBasePrice, qty);
          break;
      }

      const priceWrapper = ensurePriceWrapper(itemNode);
      priceWrapper.innerHTML = '';

      if (priceData.hasDiscount) {
        priceWrapper.innerHTML = `
          <s style="color:#999;text-decoration:line-through;margin-right:8px;font-size:14px;">
            ${fmtEUR(priceData.originalPrice)}
          </s>
          <span class="price price--end" style="font-weight:bold;font-size:16px;">
            ${fmtEUR(priceData.finalPrice)}
          </span>
          <div class="cart-drawer-item__saving" style="color:#349d92;font-weight:bold;font-size:12px;margin-top:4px;">
            Ahorro De ${fmtEUR(priceData.savings)}
          </div>
        `;
        totalSavingsCents += priceData.savings;
      } else {
        priceWrapper.innerHTML = `
          <span class="price price--end" style="font-weight:bold;font-size:16px;">
            ${fmtEUR(priceData.finalPrice)}
          </span>
        `;
      }
      
      totalVisualPrice += priceData.finalPrice;
      hideShopifyPriceNodes(itemNode);
    }

    lockGiftControlsInDOM(doc);
    updateDrawerTotals(doc, totalVisualPrice, totalSavingsCents);

    return doc.documentElement.outerHTML;
  } catch (e) {
    return htmlString;
  }
}

function updateDrawerTotals(doc, totalVisualPrice, totalSavingsCents) {
  try {
    const totalsPlaceholder = doc.querySelector('.js-cart-totals-placeholder');
    if (!totalsPlaceholder) return;

    totalsPlaceholder.innerHTML = '';

    if (totalSavingsCents > 0) {
      const ahorroRow = doc.createElement('p');
      ahorroRow.className = 'cart-drawer__totals__row cart-drawer__totals__row--center';
      ahorroRow.style.cssText = 'font-weight:bold;color:#349d92;margin-bottom:0.5rem;';
      ahorroRow.innerHTML = `
        <span>Ahorro Total De </span>
        <span class="cart-drawer__totals__row__money">${fmtEUR(totalSavingsCents)}</span>
      `;
      totalsPlaceholder.appendChild(ahorroRow);
    }

    const subtotalRow = doc.createElement('p');
    subtotalRow.className = 'cart-drawer__totals__row cart-drawer__totals__row--center';
    subtotalRow.style.cssText = 'font-weight:bold;margin-bottom:0;';
    subtotalRow.innerHTML = `
      <span>Total A Pagar: </span>
      <span class="cart-drawer__totals__row__money js-subtotal-amount">${fmtEUR(totalVisualPrice)}</span>
    `;
    totalsPlaceholder.appendChild(subtotalRow);

    const checkoutTotal = doc.querySelector('.js-checkout-total');
    if (checkoutTotal) {
      checkoutTotal.textContent = fmtEUR(totalVisualPrice);
    }
  } catch (e) {
    // Silenciar errores
  }
}

/* ==================== PROCESAMIENTO VISUAL DEL CARRITO PRINCIPAL ==================== */

function isOnCartPage() {
  return !!document.querySelector('#main-cart-items');
}

async function processCartPageVisual() {
  try {
    const cart = await getCart();
    if (!cart || !Array.isArray(cart.items)) {
      // Si no hay items, limpiar todos los elementos del DOM
      const allCartItems = document.querySelectorAll('#main-cart-items .cart-item');
      allCartItems.forEach(item => item.remove());
      return;
    }

    const cartItems = document.querySelectorAll('#main-cart-items .cart-item');
    let totalVisualPrice = 0;

    // Primero, eliminar TODOS los items fantasma que sobren
    if (cartItems.length > cart.items.length) {
      // Eliminar los items extra del DOM inmediatamente
      for (let i = cart.items.length; i < cartItems.length; i++) {
        const itemToRemove = cartItems[i];
        if (itemToRemove && itemToRemove.parentNode) {
          itemToRemove.remove();
        }
      }
    }

    // Ahora procesar solo los items que existen en el carrito
    const max = Math.min(cart.items.length, cartItems.length);

    for (let i = 0; i < max; i++) {
      const cartItem = cart.items[i];
      const itemNode = cartItems[i];
      
      // Verificar que el nodo aún existe (por si fue eliminado)
      if (!itemNode || !itemNode.parentNode) continue;
      
      const qty = Number(cartItem.quantity || 1);

      if (isGiftCartItem(cartItem)) {
        itemNode.setAttribute('data-gift', 'true');
        
        const priceData = calculateGiftVisualPrice(qty);

        const quantityInput = itemNode.querySelector('.quantity__input');
        if (quantityInput && parseInt(quantityInput.value) !== qty) {
          quantityInput.value = qty;
        }

        const unitPricePlaceholder = itemNode.querySelector('.js-unit-price-placeholder');
        if (unitPricePlaceholder) {
          unitPricePlaceholder.innerHTML = `
            <div class="product-option">
              <s style="color:#999;text-decoration:line-through;margin-right:8px;">
                ${fmtEUR(priceData.unitOriginalPrice)}
              </s>
              <span style="font-weight:bold;">${fmtEUR(priceData.unitFinalPrice)}</span>
            </div>
          `;
        }

        const linePricePlaceholders = itemNode.querySelectorAll('.js-line-price-placeholder');
        linePricePlaceholders.forEach(placeholder => {
          placeholder.innerHTML = `
            <s style="color:#999;text-decoration:line-through;margin-right:8px;">
              ${fmtEUR(priceData.originalPrice)}
            </s>
            <span class="price price--end" style="font-weight:bold;">
              ${fmtEUR(priceData.finalPrice)}
            </span>
          `;
        });

        totalVisualPrice += priceData.finalPrice;
        continue;
      }

      try {
        const niceLabel = await resolveNiceLabelForCartItem(cartItem);
        if (niceLabel) {
          const codes = [
            normalizeCode(cartItem.options_with_values?.[0]?.value),
            normalizeCode(cartItem.variant_title)
          ].filter(Boolean);

          const productOptions = itemNode.querySelectorAll('.product-option dd');
          for (const dd of productOptions) {
            const text = (dd.textContent || '').trim();
            if (codes.includes(normalizeCode(text))) { 
              dd.textContent = niceLabel; 
              break; 
            }
          }
        }
      } catch (_) {}

      const handle = extractHandleFromItem(cartItem);
      const productType = detectProductType(handle);
      
      let priceData;
      
      switch (productType) {
        case 'arnes':
          priceData = calculateArnesVisualPrice(cartItem.price, qty);
          break;
          
        case 'cama':
          priceData = calculateCamaVisualPrice(cartItem.price, qty);
          break;
          
        case 'multicantidad_simple':
        case 'multicantidad_con_variantes':
        case 'bebedero_guante_correa':
        default:
          const mqProduct = await getProductJSON(handle);
          const mqBasePrice = mqProduct?.variants?.[0]?.price || cartItem.price;
          priceData = calculateMultiQuantityVisualPrice(mqBasePrice, qty);
          break;
      }

      const unitPricePlaceholder = itemNode.querySelector('.js-unit-price-placeholder');
      if (unitPricePlaceholder) {
        if (priceData.hasDiscount) {
          unitPricePlaceholder.innerHTML = `
            <div class="product-option">
              <s style="color:#999;text-decoration:line-through;margin-right:8px;">
                ${fmtEUR(priceData.unitOriginalPrice)}
              </s>
              <span style="font-weight:bold;">${fmtEUR(priceData.unitFinalPrice)}</span>
            </div>
          `;
        } else {
          unitPricePlaceholder.innerHTML = `
            <div class="product-option">${fmtEUR(priceData.unitFinalPrice)}</div>
          `;
        }
      }

      const linePricePlaceholders = itemNode.querySelectorAll('.js-line-price-placeholder');
      linePricePlaceholders.forEach(placeholder => {
        if (priceData.hasDiscount) {
          placeholder.innerHTML = `
            <s style="color:#999;text-decoration:line-through;margin-right:8px;">
              ${fmtEUR(priceData.originalPrice)}
            </s>
            <span class="price price--end" style="font-weight:bold;">
              ${fmtEUR(priceData.finalPrice)}
            </span>
          `;
        } else {
          placeholder.innerHTML = `
            <span class="price price--end">${fmtEUR(priceData.finalPrice)}</span>
          `;
        }
      });

      totalVisualPrice += priceData.finalPrice;
    }

    lockGiftControlsInDOM(document);

    // Si el carrito está vacío, actualizar el estado vacío
    if (cart.items.length === 0) {
      const mainCartItems = document.querySelector('#main-cart-items');
      if (mainCartItems) {
        mainCartItems.innerHTML = '<p style="text-align:center;padding:2rem;">Tu carrito está vacío</p>';
      }
    }

  } catch (e) {
    console.error('Error en processCartPageVisual:', e);
  }
}

/* ==================== GESTIÓN DE REGALOS SIMPLIFICADA ==================== */

async function changeLineQtyByKey(key, newQty) {
  if (!key) return null;
  
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: newQty })
    });
    
    if (!res.ok) {
      return null;
    }
    
    return res.json();
  } catch (e) {
    return null;
  }
}

async function addGiftQuantity(variantId, qtyToAdd) {
  if (qtyToAdd <= 0) return null;
  
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: qtyToAdd,
          properties: { '_regalo_arnes': 'true' }
        }]
      })
    });
    
    if (!res.ok) throw new Error('Error añadiendo regalo');
    return res.json();
  } catch (e) {
    return null;
  }
}

async function ensureGiftSynced() {
  try {
    const cart = await getCart();
    const desiredGifts = getTotalArnesQty(cart);
    const giftLine = getGiftLine(cart);
    const currentGifts = giftLine?.quantity || 0;

    if (desiredGifts === currentGifts) {
      return { synced: true, cart };
    }

    if (desiredGifts > currentGifts) {
      const missing = desiredGifts - currentGifts;
      await addGiftQuantity(GIFT_VARIANT_ID, missing);
    } else if (desiredGifts < currentGifts && giftLine) {
      await changeLineQtyByKey(giftLine.key, desiredGifts);
    }

    await sleep(100);
    const updatedCart = await getCart();
    return { synced: true, cart: updatedCart };
  } catch (e) {
    return { synced: false, cart: null };
  }
}

/* ==================== BLOQUEO DE CONTROLES REGALO ==================== */

function lockGiftControlsInDOM(rootElement) {
  const root = rootElement || document;
  
  const giftItems = root.querySelectorAll('[data-gift="true"]');
  giftItems.forEach(giftNode => {
    // Bloquear botones de cantidad
    const quantityButtons = giftNode.querySelectorAll('.quantity__button');
    quantityButtons.forEach(btn => {
      btn.disabled = true;
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.5';
    });

    // Bloquear inputs de cantidad
    const quantityInputs = giftNode.querySelectorAll('.quantity__input');
    quantityInputs.forEach(input => {
      input.readOnly = true;
      input.style.pointerEvents = 'none';
    });
  });
}

/* ==================== BURBUJA DEL CARRITO ==================== */

function computeCartBubbleCount(cart) {
  if (!cart || !Array.isArray(cart.items)) return 0;
  
  if (BUBBLE_COUNT_INCLUDES_GIFTS) {
    return cart.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  } else {
    return cart.items.reduce((sum, item) => {
      if (isGiftCartItem(item)) return sum;
      return sum + (item.quantity || 0);
    }, 0);
  }
}

async function refreshCartIconBubble(passedCart = null) {
  try {
    const cart = passedCart || await getCart();
    const count = computeCartBubbleCount(cart);

    const cartIcons = [
      ...document.querySelectorAll('#cart-icon-bubble'),
      ...document.querySelectorAll('.header__icon--cart'),
      ...document.querySelectorAll('a[href="/cart"]')
    ];

    const uniqueIcons = [...new Set(cartIcons)];

    uniqueIcons.forEach(host => {
      if (!(host instanceof Element)) return;

      let bubble = host.querySelector('.cart-count-bubble');
      
      if (count > 0) {
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          host.appendChild(bubble);
        }
        
        bubble.innerHTML = `
          <span aria-hidden="true">${count}</span>
          <span class="visually-hidden">${count} artículos en el carrito</span>
        `;
        bubble.style.display = '';
        bubble.classList.remove('hidden');
      } else {
        if (bubble) {
          if (HIDE_BUBBLE_WHEN_ZERO) {
            bubble.remove();
          } else {
            bubble.innerHTML = `
              <span aria-hidden="true">0</span>
              <span class="visually-hidden">0 artículos en el carrito</span>
            `;
          }
        }
      }
    });
  } catch (e) {
    // Silenciar errores
  }
}

/* ==================== ACTUALIZACIÓN DEL DRAWER ==================== */

async function updateDrawerWithVisualPrices(cart) {
  try {
    if (__normalizationInProgress) {
      return;
    }

    const drawerHost = document.querySelector('cart-drawer');
    if (!drawerHost) return;

    const hasItems = cart.items.length > 0;
    drawerHost.classList.toggle('is-empty', !hasItems);

    const res = await fetch('/?sections=cart-drawer', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    
    if (!res.ok) return;
    
    const sections = await res.json();
    const processedHTML = await processDrawerHTMLVisual(sections['cart-drawer'], cart);
    
    const parser = new DOMParser();
    const drawerDoc = parser.parseFromString(processedHTML, 'text/html');
    const newDrawerNode = drawerDoc.querySelector('#CartDrawer');
    const currentDrawer = drawerHost.querySelector('#CartDrawer');
    
    if (newDrawerNode && currentDrawer) {
      currentDrawer.replaceWith(newDrawerNode);
    } else if (newDrawerNode && !currentDrawer) {
      drawerHost.appendChild(newDrawerNode);
    }

    lockGiftControlsInDOM(drawerHost);
    
  } catch (e) {
    // Silenciar errores
  }
}

async function renderEmptyState() {
  try {
    const res = await fetch('/?sections=cart-drawer', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    
    if (!res.ok) return;
    
    const sections = await res.json();
    const parser = new DOMParser();
    const drawerDoc = parser.parseFromString(sections['cart-drawer'], 'text/html');
    const newDrawerNode = drawerDoc.querySelector('#CartDrawer');
    
    const drawerHost = document.querySelector('cart-drawer');
    if (drawerHost && newDrawerNode) {
      const currentDrawer = drawerHost.querySelector('#CartDrawer');
      if (currentDrawer) {
        currentDrawer.replaceWith(newDrawerNode);
      } else {
        drawerHost.appendChild(newDrawerNode);
      }
      
      drawerHost.classList.add('is-empty');
      
      if (typeof drawerHost.open === 'function') {
        drawerHost.open();
      }
    }
    
    await refreshCartIconBubble({ items: [], item_count: 0 });
  } catch (e) {
    // Silenciar errores
  }
}

/* ==================== FUNCIONES DE SELECCIÓN DE PRODUCTO ==================== */

function getCurrentSelectedVariant() {
  let product = null;

  const productScript = document.querySelector('script[data-product-json]');
  if (productScript) {
    try {
      product = JSON.parse(productScript.textContent || productScript.innerHTML);
    } catch (_) {}
  }

  if (!product && window.ShopifyAnalytics?.meta?.product) {
    product = window.ShopifyAnalytics.meta.product;
  }

  if (!product && window.product) {
    product = window.product;
  }

  if (!product || !product.variants) return null;

  const selectedOption = document.querySelector('.dropdown-option.selected');
  console.log('🔍 DROPDOWN DEBUG:', {
    selectedOptionExists: !!selectedOption,
    dataValue: selectedOption?.getAttribute('data-value'),
    allOptions: Array.from(document.querySelectorAll('.dropdown-option')).map(opt => ({
      dataValue: opt.getAttribute('data-value'),
      hasSelected: opt.classList.contains('selected'),
      text: opt.textContent.trim()
    }))
  });
  
  if (selectedOption) {
    const selectedValue = selectedOption.getAttribute('data-value') || '';
    let variantName = selectedValue.includes('|') 
      ? selectedValue.split('|')[1].trim() 
      : selectedValue.trim();

    console.log('🔍 VARIANT MATCHING:', {
      selectedValue,
      variantName,
      searchingFor: variantName.toLowerCase()
    });

    // Búsqueda original por nombres/identifiers
    const foundVariant = product.variants.find(variant => {
      const identifiers = [
        variant.option1, variant.option2, variant.option3,
        variant.public_title, variant.title, variant.name
      ].filter(Boolean).map(id => String(id).toLowerCase().trim());
      
      console.log(`Checking variant ${variant.id}:`, { 
        title: variant.title,
        option1: variant.option1,
        option2: variant.option2, 
        option3: variant.option3,
        public_title: variant.public_title,
        identifiers, 
        matches: identifiers.some(id => id === variantName.toLowerCase() || id.includes(variantName.toLowerCase()))
      });
      
      const searchName = variantName.toLowerCase();
      return identifiers.some(id => id === searchName || id.includes(searchName));
    });
    
    if (foundVariant) {
      console.log('✅ Found variant by name:', foundVariant);
      return foundVariant;
    }

    // NUEVA LÓGICA: Búsqueda específica por tamaño usando public_title
    console.log('❌ No match by name, trying size-based search...');

    let sizeBasedVariant = null;

    if (selectedValue.includes('1,8m') || selectedValue.includes('1.8m')) {
      sizeBasedVariant = product.variants.find(v => 
        v.public_title && (v.public_title.includes('1.8') || v.public_title.includes('1,8'))
      );
      console.log('🔍 Searching for 1.8m variants:', sizeBasedVariant);
    } else if (selectedValue.includes('1,5m') || selectedValue.includes('1.5m')) {
      sizeBasedVariant = product.variants.find(v => 
        v.public_title && (v.public_title.includes('1.5') || v.public_title.includes('1,5'))
      );
      console.log('🔍 Searching for 1.5m variants:', sizeBasedVariant);
    }

    // Para otros tipos de productos, buscar por palabras clave en public_title
    if (!sizeBasedVariant) {
      if (selectedValue.toLowerCase().includes('right') || selectedValue.toLowerCase().includes('derecha')) {
        sizeBasedVariant = product.variants.find(v => 
          v.public_title && (v.public_title.toLowerCase().includes('right') || v.public_title.toLowerCase().includes('derecha'))
        );
        console.log('🔍 Searching for RIGHT hand variants:', sizeBasedVariant);
      } else if (selectedValue.toLowerCase().includes('left') || selectedValue.toLowerCase().includes('izquierda')) {
        sizeBasedVariant = product.variants.find(v => 
          v.public_title && (v.public_title.toLowerCase().includes('left') || v.public_title.toLowerCase().includes('izquierda'))
        );
        console.log('🔍 Searching for LEFT hand variants:', sizeBasedVariant);
      }
    }

    if (sizeBasedVariant) {
      console.log('✅ Found variant by size/type:', sizeBasedVariant);
      return sizeBasedVariant;
    }

    console.log('❌ No variant found by any method, using fallback');
  }

  console.log('📌 Using fallback variant:', product.variants[0]);
  return product.variants[0] || null;
}

function getCurrentSelectedQuantity() {
  const activeQuantity = document.querySelector('.quantity-option.active');
  if (activeQuantity) {
    const qty = parseInt(activeQuantity.dataset.quantity) || 1;
    return qty;
  }
  
  return 1;
}

async function getCurrentNiceLabel() {
  const currentHandle = getCurrentProductHandle();
  const productType = detectProductType(currentHandle);
  
  if (productType === 'multicantidad_simple') {
    const product = await getProductJSON(currentHandle);
    if (product && product.variants && product.variants.length === 3) {
      const baseVariant = product.variants[0];
      return baseVariant.public_title || baseVariant.title || baseVariant.option1 || null;
    }
  }
  
  if (productType === 'multicantidad_con_variantes' || productType === 'bebedero_guante_correa') {
    const selectedText = document.querySelector('.selected-text');
    if (selectedText && selectedText.textContent.trim()) {
      return selectedText.textContent.trim();
    }
  }
  
  const selectedText = document.querySelector('.selected-text');
  if (selectedText && selectedText.textContent.trim()) {
    return selectedText.textContent.trim();
  }
  
  const variant = getCurrentSelectedVariant();
  return variant?.public_title || variant?.title || null;
}

async function findConsolidationTarget(cart, variantId, currentHandle) {
  console.log('=== FIND CONSOLIDATION TARGET DEBUG ===');
  console.log('Looking for:', { variantId, currentHandle });
  console.log('Cart items:', cart.items.map(item => ({
    key: item.key,
    variant_id: item.variant_id,
    handle: extractHandleFromItem(item),
    properties: item.properties,
    isGift: isGiftCartItem(item)
  })));
  
  if (!cart || !Array.isArray(cart.items) || !currentHandle) return null;
  
  const product = await getProductJSON(currentHandle);
  if (!product || !product.variants) return null;
  
  const variantCount = product.variants.length;
  const productType = detectProductType(currentHandle);
  const isMultiQuantityProduct = ['multicantidad_simple', 'multicantidad_con_variantes', 'bebedero_guante_correa'].includes(productType);
  
  console.log('Product analysis:', { variantCount, productType, isMultiQuantityProduct });
  
  let target = null;
  
  if (isMultiQuantityProduct) {
    // Para multicantidad: buscar por grupo base, no por variant_id exacta
    const currentVariant = product.variants.find(v => v.id === variantId);
    const baseGroup = getProductBaseGroup(currentVariant, product.variants);
    
    console.log('Base group search:', { currentVariant, baseGroup });
    
    if (baseGroup) {
      target = cart.items.find(item => {
        if (isGiftCartItem(item)) return false;
        
        const itemVariant = product.variants.find(v => v.id === item.variant_id);
        const itemBaseGroup = getProductBaseGroup(itemVariant, product.variants);
        
        console.log('Comparing groups:', { itemBaseGroup, baseGroup, match: itemBaseGroup === baseGroup });
        
        return itemBaseGroup === baseGroup;
      });
    } else {
      // Fallback a búsqueda exacta si no puede determinar grupo
      target = cart.items.find(item => item.variant_id === variantId && !isGiftCartItem(item));
    }
    
    console.log('Multi-quantity group search result:', target);
  }
  
  console.log('Final target found:', target);
  console.log('=== END CONSOLIDATION DEBUG ===');
  return target;
}

/* ==================== AÑADIR PRODUCTOS AL CARRITO ==================== */

async function addProductToCart() {
  if (__operationInProgress) return;
  __operationInProgress = true;

  const variant = getCurrentSelectedVariant();
  console.log('🐛 SELECTED VARIANT DEBUG:', {
    variantId: variant?.id,
    title: variant?.title,
    dropdownText: document.querySelector('.selected-text')?.textContent
  });
  const quantity = getCurrentSelectedQuantity();
  const currentHandle = getCurrentProductHandle();
  const productType = detectProductType(currentHandle);
  const niceLabel = await getCurrentNiceLabel();

  if (!variant?.id) {
    __operationInProgress = false;
    return;
  }

  const button = document.querySelector('.comprar-ahora-btn');
  const buttonText = button?.querySelector('.btn-text');
  const originalText = buttonText?.textContent || '';
  
  if (buttonText) buttonText.textContent = 'Añadiendo...';
  if (button) {
    button.disabled = true;
    button.style.opacity = '0.7';
    button.style.pointerEvents = 'none';
  }

  function restoreButton() {
    if (buttonText) buttonText.textContent = originalText;
    if (button) {
      button.disabled = false;
      button.style.opacity = '1';
      button.style.pointerEvents = '';
    }
    __operationInProgress = false;
  }

  try {
    const cart = await getCart();
    let existingLine = null;
    
    if (productType === 'multicantidad_simple' || productType === 'multicantidad_con_variantes') {
      existingLine = await findConsolidationTarget(cart, variant.id, currentHandle);
    }

    const productProperties = {};
    if (niceLabel) {
      productProperties['Selección mostrada'] = niceLabel;
    }

    if (existingLine) {
      const newTotalQuantity = existingLine.quantity + quantity;
      
      window.__suppressDrawerOpen = true;
      
      await changeLineQtyByKey(existingLine.key, 0);
      await sleep(200);
      
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ 
            id: variant.id, 
            quantity: newTotalQuantity, 
            properties: productProperties 
          }]
        })
      });
      
      await sleep(200);
      window.__suppressDrawerOpen = false;
      
    } else {
      const itemsToAdd = [{ id: variant.id, quantity, properties: productProperties }];
      
      if (productType === 'arnes') {
        itemsToAdd.push({ 
          id: GIFT_VARIANT_ID, 
          quantity, 
          properties: { '_regalo_arnes': 'true' } 
        });
      }

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      });

      if (!addResponse.ok) {
        restoreButton();
        return;
      }
    }

    setTimeout(async () => {
      try {
        let updatedCart;
        if (productType === 'arnes') {
          const syncResult = await ensureGiftSynced();
          updatedCart = syncResult.cart;
        } else {
          updatedCart = await getCart();
        }
        
        await updateDrawerWithVisualPrices(updatedCart);
        await refreshCartIconBubble(updatedCart);
        
        if (!window.__suppressDrawerOpen) {
          const drawerHost = document.querySelector('cart-drawer');
          if (drawerHost && typeof drawerHost.open === 'function') {
            drawerHost.open();
          }
        }
        
        setTimeout(restoreButton, 300);
      } catch (e) {
        restoreButton();
      }
    }, existingLine ? 500 : 200);

  } catch (error) {
    restoreButton();
  }
}

async function addArnesWithGift() {
  return addProductToCart();
}

/* ==================== AÑADIR BUNDLE AL CARRITO ==================== */

async function addBundleToCart() {
  if (__operationInProgress) return;
  __operationInProgress = true;

  const button = document.getElementById('bundleAddToCartBtn');
  const originalText = button?.textContent || '';
  
  if (button) {
    button.textContent = 'Añadiendo...';
    button.disabled = true;
    button.style.opacity = '0.7';
  }

  function restoreButton() {
    if (button) {
      button.textContent = originalText;
      button.disabled = false;
      button.style.opacity = '1';
    }
    __operationInProgress = false;
  }

  try {
    const checkedItems = document.querySelectorAll('.bundle-checkbox-item.checked');
    if (checkedItems.length === 0) {
      restoreButton();
      return;
    }

    const cart = await getCart();
    const itemsToProcess = [];

    // Recopilar items seleccionados
    for (const item of checkedItems) {
      const productIndex = parseInt(item.getAttribute('data-product-index'));
      const productData = globalBundleProductsData[productIndex];
      const dropdown = item.querySelector('.bundle-variant-dropdown');
      const selectedValue = dropdown.value;
      
      if (!productData || !selectedValue) continue;

      const selectedVariant = productData.variants.find(v => v.value === selectedValue);
      if (!selectedVariant) continue;

      // Determinar cantidad y variante según tipo de producto
      let quantity = 1;
      let variantId = null;
      let properties = {};

      switch (productData.type) {
        case 'arnes':
        case 'cama':
          if (selectedVariant.shopifyVariantId) {
            variantId = selectedVariant.shopifyVariantId;
          }
          break;

        case 'multicantidad_simple':
          quantity = selectedVariant.quantity || 1;
          // Usar la primera variante del producto con la cantidad correcta
          const product = await getProductJSON(productData.handle);
          if (product && product.variants) {
            if (quantity === 1) variantId = product.variants[0]?.id;
            else if (quantity === 2) variantId = product.variants[1]?.id || product.variants[0]?.id;
            else variantId = product.variants[2]?.id || product.variants[1]?.id || product.variants[0]?.id;
          }
          break;

        case 'multicantidad_con_variantes':
          quantity = selectedVariant.quantity || 1;
          const variantGroup = selectedVariant.variantGroup || 0;
          const productMCV = await getProductJSON(productData.handle);
          if (productMCV && productMCV.variants) {
            // SIEMPRE usar la variante BASE del grupo seleccionado
            const baseVariantIndex = variantGroup * 3; // Siempre +0, no +quantity-1
            variantId = productMCV.variants[baseVariantIndex]?.id;
          }
          break;
      }

      if (!variantId) continue;

      // Verificar si ya existe en el carrito para consolidar
      let existingItem = null;

      const isMultiQuantityProduct = ['multicantidad_simple', 'multicantidad_con_variantes', 'bebedero_guante_correa'].includes(productData.type);

      if (isMultiQuantityProduct) {
        // Para productos multicantidad: usar lógica de grupo base
        const productMCV = await getProductJSON(productData.handle);
        if (productMCV && productMCV.variants) {
          const currentVariant = productMCV.variants.find(v => v.id === variantId);
          const baseGroup = getProductBaseGroup(currentVariant, productMCV.variants);
          
          if (baseGroup) {
            existingItem = cart.items.find(cartItem => {
              if (isGiftCartItem(cartItem)) return false;
              
              const itemVariant = productMCV.variants.find(v => v.id === cartItem.variant_id);
              const itemBaseGroup = getProductBaseGroup(itemVariant, productMCV.variants);
              
              return itemBaseGroup === baseGroup;
            });
          } else {
            // Fallback por handle para multicantidad simple
            existingItem = cart.items.find(cartItem => {
              const itemHandle = extractHandleFromItem(cartItem);
              return itemHandle === productData.handle && !isGiftCartItem(cartItem);
            });
          }
        }
      } else {
        // Para arnés/cama: consolidar por variant_id exacta
        existingItem = cart.items.find(cartItem => {
          return cartItem.variant_id === variantId && !isGiftCartItem(cartItem);
        });
      }

      if (existingItem) {
        // Consolidar con item existente
        itemsToProcess.push({
          action: 'update',
          key: existingItem.key,
          variantId: variantId,
          quantity: existingItem.quantity + quantity,
          properties: existingItem.properties || {}
        });
      } else {
        // Añadir como nuevo item
        itemsToProcess.push({
          action: 'add',
          variantId: variantId,
          quantity: quantity,
          properties: properties
        });

        // Si es arnés, añadir regalo
        if (productData.type === 'arnes') {
          itemsToProcess.push({
            action: 'add',
            variantId: GIFT_VARIANT_ID,
            quantity: quantity,
            properties: { '_regalo_arnes': 'true' }
          });
        }
      }
    }

    // Ejecutar operaciones de carrito
    window.__suppressCartHook__ = true;

    for (const item of itemsToProcess) {
      if (item.action === 'update') {
        // Eliminar el item existente
        await changeLineQtyByKey(item.key, 0);
        await sleep(150);
        
        // Añadir con nueva cantidad
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: item.variantId,
              quantity: item.quantity,
              properties: item.properties
            }]
          })
        });
      } else {
        // Añadir nuevo item
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: item.variantId,
              quantity: item.quantity,
              properties: item.properties
            }]
          })
        });
      }
      await sleep(100);
    }

    window.__suppressCartHook__ = false;

    // Actualizar interfaz
    setTimeout(async () => {
      try {
        const { cart: updatedCart } = await ensureGiftSynced();
        await updateDrawerWithVisualPrices(updatedCart);
        await refreshCartIconBubble(updatedCart);
        
        const drawerHost = document.querySelector('cart-drawer');
        if (drawerHost && typeof drawerHost.open === 'function') {
          drawerHost.open();
        }
        
        setTimeout(restoreButton, 300);
      } catch (e) {
        restoreButton();
      }
    }, 500);

  } catch (error) {
    console.error('Error añadiendo bundle:', error);
    restoreButton();
  }
}

/* ==================== EXPONER FUNCIÓN PARA BUNDLE ==================== */
// Exponer función para uso desde desplegable-product.js
window.addBundleToCart = addBundleToCart;

// Exponer función para uso desde otros scripts
window.processCartPageVisual = processCartPageVisual;

/* ==================== NORMALIZACIÓN PRE-CHECKOUT MEJORADA ==================== */

function findCorrectVariantForQuantity(variants, currentVariantId, targetQuantity) {
  if (!variants || variants.length !== 6) return null;
  
  const currentVariant = variants.find(v => v.id === currentVariantId);
  if (!currentVariant) return null;
  
  console.log('=== FIND CORRECT VARIANT DEBUG ===');
  console.log('Current variant ID:', currentVariantId);
  console.log('Current variant:', currentVariant);
  console.log('Target quantity:', targetQuantity);
  console.log('All variants:', variants);
  
  // LÓGICA MEJORADA PARA DETECTAR GRUPOS
  let colorGroup = [];
  
  // 1. DETECTAR POR PUBLIC_TITLE (más confiable)
  if (currentVariant.public_title) {
    const currentTitle = currentVariant.public_title.replace(/[.,]$/, '').toLowerCase();
    
    // Para guantes: por hand type (español/inglés)
    const isGlove = currentTitle.includes('hand') || currentTitle.includes('mano') || 
                    currentTitle.includes('guante') || currentTitle.includes('derecha') || 
                    currentTitle.includes('izquierda');

    if (isGlove) {
      let handType = '';
      
      // Detectar tipo de mano en español o inglés
      if (currentTitle.includes('right') || currentTitle.includes('derecha')) {
        handType = 'derecha';
      } else if (currentTitle.includes('left') || currentTitle.includes('izquierda')) {
        handType = 'izquierda';
      } else if (currentTitle.includes('hand')) {
        // Fallback para inglés
        handType = currentTitle.includes('right') ? 'derecha' : 'izquierda';
      }
      
      if (handType) {
        colorGroup = variants.filter(v => {
          const vTitle = (v.public_title || '').toLowerCase();
          // Buscar tanto en español como inglés
          const isRightHand = vTitle.includes('derecha') || vTitle.includes('right');
          const isLeftHand = vTitle.includes('izquierda') || vTitle.includes('left');
          
          if (handType === 'derecha') {
            return isRightHand;
          } else if (handType === 'izquierda') {
            return isLeftHand;
          }
          return false;
        });
        console.log('🧤 GUANTE GROUP (by hand):', colorGroup.length, 'variants, type:', handType);
      }
    }
    // Para otros productos: por color exacto
    else {
      colorGroup = variants.filter(v => {
        const vTitle = (v.public_title || '').replace(/[.,]$/, '').toLowerCase();
        return vTitle === currentTitle;
      });
      console.log('🎨 COLOR GROUP (by exact match):', colorGroup.length, 'variants');
    }
  }
  
  // 2. FALLBACK: DETECTAR POR PALABRAS CLAVE DE COLOR
  if (colorGroup.length !== 3 && currentVariant.public_title) {
    const currentTitle = currentVariant.public_title.toLowerCase();
    const colorWords = ['negro', 'blanco', 'azul', 'rojo', 'verde', 'rosa', 'gris', 'amarillo', 'black', 'white', 'blue', 'red', 'green', 'pink', 'gray', 'yellow'];
    
    let detectedColor = null;
    for (const color of colorWords) {
      if (currentTitle.includes(color)) {
        detectedColor = color;
        break;
      }
    }
    
    if (detectedColor) {
      colorGroup = variants.filter(v => {
        const vTitle = (v.public_title || '').toLowerCase();
        return vTitle.includes(detectedColor);
      });
      console.log('🎨 COLOR GROUP (by keyword):', colorGroup.length, 'variants, keyword:', detectedColor);
    }
  }
  
  // 3. FALLBACK: DETECTAR POR OPTION1, OPTION2, OPTION3
  if (colorGroup.length !== 3) {
    // Intentar por option1
    if (currentVariant.option1) {
      colorGroup = variants.filter(v => v.option1 === currentVariant.option1);
      console.log('📋 OPTION1 GROUP:', colorGroup.length, 'variants');
    }
    
    // Si no funciona, intentar por option2
    if (colorGroup.length !== 3 && currentVariant.option2) {
      colorGroup = variants.filter(v => v.option2 === currentVariant.option2);
      console.log('📋 OPTION2 GROUP:', colorGroup.length, 'variants');
    }
    
    // Si no funciona, intentar por option3
    if (colorGroup.length !== 3 && currentVariant.option3) {
      colorGroup = variants.filter(v => v.option3 === currentVariant.option3);
      console.log('📋 OPTION3 GROUP:', colorGroup.length, 'variants');
    }
  }
  
  if (colorGroup.length !== 3) {
    console.log('❌ Could not find valid group of 3 variants');
    console.log('Final result:', null);
    console.log('=== END FIND CORRECT VARIANT DEBUG ===');
    return null;
  }
  
  // Ordenar por precio descendente (base, -10%, -20%)
  colorGroup.sort((a, b) => b.price - a.price);
  
  console.log('✅ Found group of 3, sorted by price:');
  colorGroup.forEach((v, i) => {
    console.log(`  [${i}] ID: ${v.id}, Price: ${v.price}, Title: ${v.public_title || v.title}`);
  });
  
  let targetVariant;
  if (targetQuantity === 1) {
    targetVariant = colorGroup[0]; // Precio base
  } else if (targetQuantity === 2) {
    targetVariant = colorGroup[1]; // Precio -10%
  } else {
    targetVariant = colorGroup[2]; // Precio -20%
  }
  
  console.log('🎯 Selected target variant:', targetVariant);
  console.log('Final result:', targetVariant);
  console.log('=== END FIND CORRECT VARIANT DEBUG ===');
  
  return targetVariant;
}

async function normalizeCartForCheckout() {
  try {
    __normalizationInProgress = true;
    showDrawerProcessingState();
    
    const cart = await getCart();
    if (!cart || !cart.items.length) {
      hideDrawerProcessingState();
      __normalizationInProgress = false;
      return true;
    }

    const updates = [];

    for (const item of cart.items) {
      if (isGiftCartItem(item)) {
        continue;
      }
      
      const handle = extractHandleFromItem(item);
      const product = await getProductJSON(handle);
      if (!product || !product.variants) {
        continue;
      }
      
      const qty = item.quantity;
      const variantCount = product.variants.length;

      if (variantCount === 3) {
        let targetVariantIndex;
        if (qty === 1) targetVariantIndex = 0;
        else if (qty === 2) targetVariantIndex = 1;
        else targetVariantIndex = 2;

        const targetVariant = product.variants[targetVariantIndex];
        if (!targetVariant || targetVariant.id === item.variant_id) {
          continue;
        }

        updates.push({
          action: 'remove',
          key: item.key,
          reason: `Multicantidad simple: ${qty} uds`
        });
        
        updates.push({
          action: 'add',
          variantId: targetVariant.id,
          quantity: qty,
          properties: item.properties,
          reason: `Nueva variante para ${qty} uds`
        });
        
      } else if (variantCount === 6) {
        const targetVariant = findCorrectVariantForQuantity(product.variants, item.variant_id, qty);
        if (!targetVariant || targetVariant.id === item.variant_id) {
          continue;
        }

        updates.push({
          action: 'remove',
          key: item.key,
          reason: `Multicantidad desplegable: ${qty} uds`
        });
        
        updates.push({
          action: 'add',
          variantId: targetVariant.id,
          quantity: qty,
          properties: item.properties,
          reason: `Nueva variante para ${qty} uds del mismo color`
        });
      }
    }

    for (const update of updates) {
      if (update.action === 'remove') {
        await changeLineQtyByKey(update.key, 0);
      } else if (update.action === 'add') {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: update.variantId,
              quantity: update.quantity,
              properties: update.properties || {}
            }]
          })
        });
      }
      
      await sleep(200);
    }

    hideDrawerProcessingState();
    __normalizationInProgress = false;
    
    return true;
  } catch (e) {
    hideDrawerProcessingState();
    __normalizationInProgress = false;
    
    return false;
  }
}

function getProductBaseGroup(variant, allVariants) {
  if (!variant || !allVariants) return null;
  
  // Para guantes: agrupar por Right/Left Hand (español/inglés)
  if (variant.public_title) {
    const title = variant.public_title.toLowerCase();
    const isGlove = title.includes('hand') || title.includes('mano') || 
                    title.includes('guante') || title.includes('derecha') || 
                    title.includes('izquierda');
    
    if (isGlove) {
      if (title.includes('right') || title.includes('derecha')) {
        return 'hand_right';
      } else if (title.includes('left') || title.includes('izquierda')) {
        return 'hand_left';
      }
    }
  }
  
  // Para correas: agrupar por tamaño
  if (variant.public_title && variant.public_title.includes('1.')) {
    if (variant.public_title.includes('1.5')) return 'size_1.5';
    if (variant.public_title.includes('1.8')) return 'size_1.8';
  }
  
  // Para bebederos y otros productos: agrupar por color exacto (sin puntos/comas)
  if (variant.public_title) {
    const cleanTitle = variant.public_title.replace(/[.,]$/, '').toLowerCase();
    
    // Mapeo específico de colores
    const colorMap = {
      'negro': 'color_black',
      'blanco': 'color_white', 
      'black': 'color_black',
      'white': 'color_white',
      'azul': 'color_blue',
      'blue': 'color_blue',
      'rojo': 'color_red',
      'red': 'color_red'
    };
    
    // Buscar coincidencia exacta primero
    if (colorMap[cleanTitle]) {
      return colorMap[cleanTitle];
    }
    
    // Buscar por palabras clave
    for (const [keyword, colorCode] of Object.entries(colorMap)) {
      if (cleanTitle.includes(keyword)) {
        return colorCode;
      }
    }
    
    // Fallback: usar el título limpio como grupo
    return `title_${cleanTitle}`;
  }
  
  // NUEVO: Para productos multicantidad simple (3 variantes)
  if (allVariants.length === 3) {
    // Usar el primer variant como grupo base para todos los productos de 3 variantes
    return `base_${allVariants[0].id}`;
  }
  
  return null;
}

/* ==================== INTERCEPTOR DE OPERACIONES CARRITO ==================== */

(function setupCartInterceptor() {
  if (window.__cartInterceptorPatched) return;
  window.__cartInterceptorPatched = true;

  const originalFetch = window.fetch;
  
  window.fetch = async function(input, init) {
    const url = typeof input === 'string' ? input : (input?.url || '');
    const isCartOperation = /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url);

    const response = await originalFetch.apply(this, arguments);

    if (isCartOperation && response.ok && !window.__suppressCartHook__ && !window.__suppressDrawerOpen && !__normalizationInProgress) {
      setTimeout(async () => {
        try {
          const { cart } = await ensureGiftSynced();
          
          // Delay adicional para asegurar que el servidor procese todo
          await sleep(200);
          const finalCart = await getCart();
          
          if (!window.__suppressDrawerOpen) {
            if (finalCart.items.length === 0) {
              // Si estamos en la página del carrito, mostrar estado vacío
              const isCartPage = document.querySelector('#main-cart-items');
              if (isCartPage) {
                const mainCartItems = document.querySelector('#main-cart-items');
                if (mainCartItems) {
                  mainCartItems.innerHTML = '<p style="text-align:center;padding:2rem;">Tu carrito está vacío</p>';
                }
              } else {
                await renderEmptyState();
              }
            } else {
              await updateDrawerWithVisualPrices(finalCart);
              
              const isCartPage = document.querySelector('#main-cart-items');
              if (isCartPage) {
                await processCartPageVisual();
              }
            }
          }
          
          await refreshCartIconBubble(finalCart);
          
        } catch (e) {
          // Silenciar errores
        }
      }, 300); // Aumentar el delay inicial también
    }

    return response;
  };
})();

/* ==================== INTERCEPTOR ESPECÍFICO PARA REGALO FANTASMA ==================== */

(function setupRemovalInterceptor() {
  if (window.__removalInterceptorPatched) return;
  window.__removalInterceptorPatched = true;

  const originalFetch = window.fetch;
  
  window.fetch = async function(input, init) {
    const url = typeof input === 'string' ? input : (input?.url || '');
    const isCartOperation = /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url);

    const response = await originalFetch.apply(this, arguments);

    if (isCartOperation && response.ok && !window.__suppressCartHook__ && !window.__suppressDrawerOpen && !__normalizationInProgress) {
      setTimeout(async () => {
        try {
          const { cart } = await ensureGiftSynced();
          
          if (!window.__suppressDrawerOpen) {
            if (cart.items.length === 0) {
              await renderEmptyState();
            } else {
              await updateDrawerWithVisualPrices(cart);
              
              // SIMPLIFICADO: NO procesar página del carrito aquí
              // El slider se encargará del refresh si es necesario
              //const isCartPage = document.querySelector('#main-cart-items');
              //if (isCartPage) {
              //  console.log('🔧 INTERCEPTOR: Página del carrito detectada - delegando al slider');
              //}
              const isCartPage = document.querySelector('#main-cart-items');
              if (isCartPage) {
                await processCartPageVisual();
              }
            }
          }
          
          await refreshCartIconBubble(cart);
          
        } catch (e) {
          console.error('Error en interceptor:', e);
        }
      }, 300);
    }

    return response;
  };

  async function handleRemovalCleanup() {
    try {
      await sleep(600);
      
      const { cart } = await ensureGiftSynced();
      
      if (cart.items.length === 0) {
        if (isOnCartPage()) {
          window.location.reload();
          return;
        }
      } else {
        if (isOnCartPage()) {
          const container = document.querySelector('#main-cart-items');
          if (container) {
            const secId = container.dataset.id || 'main-cart-items';
            const res = await fetch(`/?sections=${encodeURIComponent(secId)}`, {
              headers: { 'X-Requested-With': 'XMLHttpRequest' }
            });
            
            if (res.ok) {
              const sections = await res.json();
              const html = sections[secId] || sections['main-cart-items'] || '';
              
              if (html) {
                container.outerHTML = html;
                lockGiftControlsInDOM(document.querySelector('#main-cart-items'));
              }
            }
          }
        }
      }
      
      await refreshCartIconBubble(cart);
      
    } catch (e) {
      if (isOnCartPage()) {
        window.location.reload();
      }
    }
  }
})();

/* ==================== EVENT LISTENERS ==================== */

document.addEventListener('click', (e) => {
  if (e.target.closest('.quantity__button')) {
    setTimeout(async () => {
      try {
        // Delay más largo para página del carrito
        const isCartPage = isOnCartPage();
        const delay = isCartPage ? 500 : 150;
        
        await sleep(delay);
        const { cart } = await ensureGiftSynced();
        
        await updateDrawerWithVisualPrices(cart);
        
        if (isCartPage) {
          await processCartPageVisual();
        }
        
        await refreshCartIconBubble(cart);
      } catch (e) {
        // Silenciar errores
      }
    }, 150);
  }
  
  if (e.target.closest('cart-remove-button button, button[id^="CartDrawer-Remove-"], button[id^="Remove-"]')) {
    setTimeout(async () => {
      try {
        const { cart } = await ensureGiftSynced();
        
        if (cart.items.length === 0) {
          await renderEmptyState();
        } else {
          await updateDrawerWithVisualPrices(cart);
          
          if (isOnCartPage()) {
            await processCartPageVisual();
          }
        }
        
        await refreshCartIconBubble(cart);
      } catch (e) {
        // Silenciar errores
      }
    }, 250);
  }
});

document.addEventListener('change', (e) => {
  if (e.target.matches('.quantity__input')) {
    setTimeout(async () => {
      try {
        const { cart } = await ensureGiftSynced();
        
        await updateDrawerWithVisualPrices(cart);
        
        if (isOnCartPage()) {
          await processCartPageVisual();
        }
        
        await refreshCartIconBubble(cart);
      } catch (e) {
        // Silenciar errores
      }
    }, 150);
  }
});

document.addEventListener('input', (e) => {
  if (e.target.matches('.quantity__input')) {
    setTimeout(async () => {
      try {
        const cart = await getCart();
        await refreshCartIconBubble(cart);
        
        if (isOnCartPage()) {
          await processCartPageVisual();
        }
      } catch (e) {
        // Silenciar errores
      }
    }, 50);
  }
});

/* ==================== BLOQUEO PREVENTIVO DE CONTROLES REGALO ==================== */

(function setupGiftControlBlocking() {
  function isGiftElement(element) {
    const host = element.closest('.cart-item, li, [data-cart-item]');
    if (!host) return false;
    
    return host.matches('[data-gift="true"]') ||
           host.textContent.toLowerCase().includes('regalo') ||
           host.textContent.includes('_regalo_arnes');
  }

  document.addEventListener('click', (e) => {
    // Mejorar la detección de botones de eliminación
    const removeButton = e.target.closest('cart-remove-button button, button[id^="CartDrawer-Remove-"], button[id^="Remove-"], .cart__remove button, .cart__remove a');
    
    if (removeButton) {
      // Esperar más tiempo para asegurar que el servidor ha procesado la eliminación
      setTimeout(async () => {
        try {
          const { cart } = await ensureGiftSynced();
          
          // Para la página del carrito, hacer una actualización completa
          if (isOnCartPage()) {
            // Primero actualizar visualmente
            await processCartPageVisual();
            
            // Si el carrito está vacío, considerar recargar la página
            if (cart.items.length === 0) {
              // Opcional: recargar la página para mostrar el estado vacío correcto
              // window.location.reload();
              
              // O actualizar manualmente el estado vacío
              const mainCartItems = document.querySelector('#main-cart-items');
              if (mainCartItems) {
                mainCartItems.innerHTML = '<p style="text-align:center;padding:2rem;">Tu carrito está vacío</p>';
              }
            }
          }
          
          // Para el drawer
          if (!isOnCartPage()) {
            if (cart.items.length === 0) {
              await renderEmptyState();
            } else {
              await updateDrawerWithVisualPrices(cart);
            }
          }
          
          await refreshCartIconBubble(cart);
        } catch (e) {
          console.error('Error procesando eliminación:', e);
        }
      }, 350); // Aumentar el delay para dar más tiempo al servidor
    }
  });

  document.addEventListener('submit', (e) => {
    if (e.target.matches('form[action*="/cart/change"]') && isGiftElement(e.target)) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  }, true);
})();

/* ==================== INTERCEPTOR DE CHECKOUT ==================== */

function setupCheckoutInterceptor() {
  document.addEventListener('click', async (e) => {
    const checkoutButton = e.target.closest('#CartDrawer-Checkout, button[name="checkout"], .cart__checkout-button');
    if (!checkoutButton) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const originalText = checkoutButton.textContent;
    checkoutButton.textContent = 'Procesando...';
    checkoutButton.disabled = true;
    
    try {
      const success = await normalizeCartForCheckout();
      
      if (success) {
        window.location.href = '/checkout';
      } else {
        window.location.href = '/checkout';
      }
    } catch (e) {
      window.location.href = '/checkout';
    }
  }, true);
}

/* ==================== INICIALIZACIÓN Y EVENTOS PRINCIPALES ==================== */

document.addEventListener('click', (e) => {
  const comprarButton = e.target.closest('.comprar-ahora-btn');
  if (!comprarButton) return;
  
  e.preventDefault();
  e.stopPropagation();
  
  addProductToCart();
}, true);

document.addEventListener('DOMContentLoaded', function() {
  setupCheckoutInterceptor();
  
  setTimeout(async () => {
    try {
      const cart = await getCart();
      
      if (cart.items.length > 0) {
        const drawerHost = document.querySelector('cart-drawer');
        if (drawerHost && drawerHost.querySelector('.cart-item')) {
          await updateDrawerWithVisualPrices(cart);
        }
        
        if (document.querySelector('#main-cart-items')) {
          await processCartPageVisual();
        }
      }
      
      await refreshCartIconBubble(cart);
      lockGiftControlsInDOM();
      
    } catch (e) {
      // Silenciar errores
    }
  }, 500);
});

(function initCartBubbleHydration() {
  let hydrated = false;

  async function hydrateBubble() {
    if (hydrated) return;
    
    try {
      await refreshCartIconBubble();
      document.documentElement.classList.add('nc-bubble-hydrated');
      hydrated = true;
    } catch (e) {
      // Silenciar errores
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateBubble, { once: true });
  } else {
    hydrateBubble();
  }

  window.addEventListener('pageshow', hydrateBubble);
})();


/* ==================== DEBUGGING FUNCTION ==================== */

async function debugVariantStructure() {
  // Solo ejecutar en páginas de productos
  if (!window.location.pathname.includes('/products/')) return;
  
  const currentHandle = getCurrentProductHandle();
  if (!currentHandle) return;
  
  try {
    const product = await getProductJSON(currentHandle);
    if (!product || !product.variants || product.variants.length !== 6) return;
    
    console.log('\n🔍 === VARIANT STRUCTURE DEBUGGING ===');
    console.log('Product Handle:', currentHandle);
    console.log('Product Title:', product.title);
    console.log('Total Variants:', product.variants.length);
    
    console.log('\n📋 All Variants:');
    product.variants.forEach((variant, index) => {
      console.log(`  [${index}]`, {
        id: variant.id,
        title: variant.title,
        option1: variant.option1,
        option2: variant.option2,
        option3: variant.option3,
        price: variant.price,
        public_title: variant.public_title
      });
    });
    
    // Simular agrupación por option1
    const option1Groups = {};
    product.variants.forEach(v => {
      const key = v.option1;
      if (!option1Groups[key]) option1Groups[key] = [];
      option1Groups[key].push(v);
    });
    
    console.log('\n🔗 Grouping by Option1:');
    Object.entries(option1Groups).forEach(([key, variants]) => {
      console.log(`  "${key}": ${variants.length} variants`);
    });
    
    // Simular agrupación por option2
    const option2Groups = {};
    product.variants.forEach(v => {
      const key = v.option2;
      if (!option2Groups[key]) option2Groups[key] = [];
      option2Groups[key].push(v);
    });
    
    console.log('\n🔗 Grouping by Option2:');
    Object.entries(option2Groups).forEach(([key, variants]) => {
      console.log(`  "${key}": ${variants.length} variants`);
    });
    
    // Simular agrupación por option3
    const option3Groups = {};
    product.variants.forEach(v => {
      const key = v.option3;
      if (!option3Groups[key]) option3Groups[key] = [];
      option3Groups[key].push(v);
    });
    
    console.log('\n🔗 Grouping by Option3:');
    Object.entries(option3Groups).forEach(([key, variants]) => {
      console.log(`  "${key}": ${variants.length} variants`);
    });
    
    // Buscar grupos de exactamente 3
    const validGroups = [];
    
    ['option1', 'option2', 'option3'].forEach(optionKey => {
      const groups = optionKey === 'option1' ? option1Groups : 
                     optionKey === 'option2' ? option2Groups : option3Groups;
                     
      Object.entries(groups).forEach(([key, variants]) => {
        if (variants.length === 3) {
          validGroups.push({
            option: optionKey,
            value: key,
            variants: variants.map(v => ({ id: v.id, price: v.price, title: v.title }))
          });
        }
      });
    });
    
    console.log('\n✅ Valid Groups (exactly 3 variants):');
    if (validGroups.length === 0) {
      console.log('  ❌ NO VALID GROUPS FOUND - This explains the consolidation bug!');
    } else {
      validGroups.forEach((group, index) => {
        console.log(`  [${index}] ${group.option} = "${group.value}"`);
        group.variants.forEach(v => console.log(`    - ${v.title} (${v.price})`));
      });
    }
    
    // Detectar palabras clave de color
    const colorWords = ['negro', 'blanco', 'azul', 'rojo', 'verde', 'rosa', 'gris', 'amarillo', 'black', 'white', 'blue', 'red', 'green', 'pink', 'gray', 'yellow'];
    
    console.log('\n🎨 Color Detection:');
    product.variants.forEach(variant => {
      const title = (variant.title || '').toLowerCase();
      const detectedColors = colorWords.filter(color => title.includes(color));
      if (detectedColors.length > 0) {
        console.log(`  ${variant.title}: detected colors [${detectedColors.join(', ')}]`);
      }
    });
    
    console.log('=== END DEBUGGING ===\n');
    
  } catch (error) {
    console.error('Error in variant debugging:', error);
  }
}

// Ejecutar automáticamente al cargar la página
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(debugVariantStructure, 1000);
});

